import { useEffect, useState } from 'react';
import {
  Button,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  RadioCardGroup,
  RadioCardItem,
} from '@eureca/ui';

export interface StageReopenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `true` = Sim (reabre p/ quem já concluiu), `false` = Não. */
  onDecision: (reopen: boolean) => void;
}

/**
 * RN-06 / RN-07 — "Deseja reabrir a etapa".
 *
 * Quando o consultor salva a edição de uma etapa que **já tem candidatos que a
 * concluíram**, este modal pede a decisão de reabertura antes da alteração ser
 * persistida:
 *
 * - **Sim** (RN-08) → o backend reabre a etapa para quem já concluiu,
 *   permitindo nova resposta.
 * - **Não** (RN-09) → a alteração vale só para candidatos novos / que ainda
 *   não concluíram; quem já concluiu mantém a versão antiga.
 *
 * O layout segue o Figma (node 17186-6607): a escolha é feita em dois
 * radio-cards Sim/Não e o consultor confirma em "Salvar alterações". Fechar o
 * modal pelo "Voltar" / X descarta a edição (equivale a cancelar). Confirmar
 * sem marcar Sim/Não exibe "Marque uma opção para continuar." e não avança.
 *
 * A decisão é transmitida ao Core no campo `modulesToReopen` do
 * `PUT /jobs/:id/steps` (Sim → `[stageId]`, Não → `[]`); o Core reseta
 * `module_user_response.completed_at` dos concluídos no "Sim". Ver
 * `docs/core-stage-reopen-implementation.md` (RN-08/RN-09).
 */
export function StageReopenDialog({ open, onOpenChange, onDecision }: StageReopenDialogProps) {
  // Escolha do radio — reseta sempre que o modal fecha para não vazar a
  // seleção da etapa anterior na próxima edição.
  const [choice, setChoice] = useState<'sim' | 'nao' | ''>('');
  // SEL — "Bloquear submit do modal de reabrir sem seleção": em vez de
  // desabilitar o botão, deixamos clicável e validamos no submit, exibindo a
  // mensagem literal do AC quando nenhuma opção foi marcada.
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) {
      setChoice('');
      setError('');
    }
  }, [open]);

  function handleSubmit() {
    if (!choice) {
      setError('Marque uma opção para continuar.');
      return;
    }
    onDecision(choice === 'sim');
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 lg:max-w-[423px]">
        <DialogHeader className="gap-1.5 px-6 pt-6 pb-6">
          <DialogTitle>Deseja reabrir a etapa</DialogTitle>
          <DialogDescription>
            Após salvar sua edição, deseja reabrir esta fase para os candidatos que já a concluíram?
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6">
          <RadioCardGroup
            value={choice}
            onValueChange={(value) => {
              setChoice(value as 'sim' | 'nao');
              setError('');
            }}
            className="flex flex-row gap-3"
            data-testid="stage-reopen-choice"
          >
            <ReopenOption value="sim" label="Sim" />
            <ReopenOption value="nao" label="Não" />
          </RadioCardGroup>
          {error && (
            <p className="mt-2 text-sm text-destructive" data-testid="stage-reopen-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="px-6 pb-6">
          <DialogClose asChild>
            <Button variant="outline" data-testid="stage-reopen-back">
              Voltar
            </Button>
          </DialogClose>
          {/* "Salvar alterações" fica sempre clicável; sem seleção o submit
              exibe "Marque uma opção para continuar." em vez de avançar. */}
          <Button onClick={handleSubmit} data-testid="stage-reopen-submit">
            Salvar alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ReopenOptionProps {
  value: string;
  label: string;
}

/** Sim/Não card — mesmo padrão do `ResubmitOption` do `PhaseSheet`. */
function ReopenOption({ value, label }: ReopenOptionProps) {
  return (
    <RadioCardItem
      value={value}
      className={cn('group flex-1 justify-start rounded-xl p-4 hover:border-primary/60')}
    >
      <span className="text-sm font-medium leading-none text-foreground">{label}</span>
    </RadioCardItem>
  );
}
